import React, { useState, useCallback } from 'react';

const ImageUpload = ({
  images = [],
  onImagesChange,
  maxImages = 10,
  title = 'Upload Images',
  description = 'Drag and drop images or click to upload'
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Cloudinary configuration from environment variables
  const CLOUDINARY_CLOUD_NAME =
    process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dwoubdppz';
  const CLOUDINARY_UPLOAD_PRESET =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'ml_default';

  const uploadToCloudinary = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('cloud_name', CLOUDINARY_CLOUD_NAME);

      try {
        console.log('Uploading to Cloudinary:', {
          cloud_name: CLOUDINARY_CLOUD_NAME,
          upload_preset: CLOUDINARY_UPLOAD_PRESET,
          file_name: file.name,
          file_size: file.size
        });

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
          {
            method: 'POST',
            body: formData
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            'Cloudinary upload failed:',
            response.status,
            errorText
          );

          // Provide more helpful error messages
          if (errorText.includes('Upload preset must be whitelisted')) {
            throw new Error(
              'Upload preset not configured for unsigned uploads. ' +
                'Please go to your Cloudinary dashboard > Settings > Upload > Upload presets ' +
                'and create a new preset with "Signing Mode" set to "Unsigned". ' +
                'Then update your .env file with REACT_APP_CLOUDINARY_UPLOAD_PRESET=your_new_preset_name'
            );
          } else if (errorText.includes('File size too large')) {
            throw new Error(
              'File size too large. Please use images under 10MB.'
            );
          } else if (errorText.includes('Invalid file type')) {
            throw new Error(
              'Invalid file type. Please use JPG, PNG, GIF, or WebP images.'
            );
          } else {
            throw new Error(`Upload failed: ${response.status} - ${errorText}`);
          }
        }

        const data = await response.json();
        console.log('Cloudinary upload success:', data);

        return {
          url: data.secure_url,
          publicId: data.public_id,
          alt: file.name,
          width: data.width,
          height: data.height
        };
      } catch (error) {
        console.error('Upload error:', error);
        throw error;
      }
    },
    [CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET]
  );

  const handleFileUpload = useCallback(
    async (files) => {
      if (images.length + files.length > maxImages) {
        alert(`Maximum ${maxImages} images allowed`);
        return;
      }

      setUploading(true);
      const newImages = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          try {
            const uploadedImage = await uploadToCloudinary(file);
            newImages.push(uploadedImage);
          } catch (error) {
            console.error('Upload failed for:', file.name, error);
            // Show error message to user
            alert(`Failed to upload ${file.name}: ${error.message}`);
          }
        }
      }

      onImagesChange([...images, ...newImages]);
      setUploading(false);
    },
    [images, maxImages, onImagesChange, uploadToCloudinary]
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileUpload(Array.from(e.dataTransfer.files));
      }
    },
    [handleFileUpload]
  );

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(Array.from(e.target.files));
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const updateImageAlt = (index, alt) => {
    const newImages = [...images];
    newImages[index] = { ...newImages[index], alt };
    onImagesChange(newImages);
  };

  const moveImage = (fromIndex, toIndex) => {
    const newImages = [...images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    onImagesChange(newImages);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />

        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48">
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="text-gray-600">
            <span className="font-medium">Click to upload</span> or drag and
            drop
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF, WebP up to 10MB each
            </p>
            <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
              <div className="flex items-center">
                <svg
                  className="w-3 h-3 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Secure upload
              </div>
              <div className="flex items-center">
                <svg
                  className="w-3 h-3 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Auto-optimized
              </div>
            </div>
          </div>
          {uploading && (
            <div className="space-y-2">
              <div className="text-blue-600 text-sm font-medium">
                📤 Uploading images...
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: '60%' }}></div>
              </div>
              <p className="text-xs text-gray-500">
                Please wait while your images are being uploaded to Cloudinary
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Image Gallery */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">
              Section Images ({images.length}/{maxImages})
            </h4>
            <div className="flex items-center space-x-2">
              <div className="flex items-center text-xs text-green-600">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                All images uploaded successfully
              </div>
            </div>
          </div>

          {/* Images Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <div
                key={index}
                className="relative group bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                {/* Image Container */}
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={image.url}
                    alt={image.alt || 'Section image'}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                    onError={(e) => {
                      console.log('Image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully:', image.url);
                    }}
                  />

                  {/* Upload Status Badge */}
                  <div className="absolute top-2 left-2">
                    <div className="flex items-center px-2 py-1 bg-green-500 text-white text-xs rounded-full shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Uploaded
                    </div>
                  </div>

                  {/* Delete Button - Always Visible */}
                  <div className="absolute top-2 right-2">
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-lg"
                      title="Delete image">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Move Buttons - On Hover */}
                  <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => moveImage(index, index - 1)}
                        className="bg-white text-gray-700 rounded p-1 hover:bg-gray-100 shadow-lg"
                        title="Move up">
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>
                    )}
                    {index < images.length - 1 && (
                      <button
                        type="button"
                        onClick={() => moveImage(index, index + 1)}
                        className="bg-white text-gray-700 rounded p-1 hover:bg-gray-100 shadow-lg"
                        title="Move down">
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-3 space-y-2">
                  {/* File Name */}
                  <div className="text-xs text-gray-500 truncate">
                    {image.alt || 'Untitled image'}
                  </div>

                  {/* Alt Text Input */}
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Description (SEO)
                    </label>
                    <textarea
                      value={image.alt || ''}
                      onChange={(e) => updateImageAlt(index, e.target.value)}
                      placeholder="Describe this image for SEO..."
                      rows={2}
                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    />
                    <p className="text-xs text-gray-400">
                      {image.alt ? `${image.alt.length} chars` : '0 chars'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {images.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-gray-500 font-medium">
                No images uploaded yet
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Upload images to enhance this section
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
